/**
 * Build NC (National Contract) save payload - form_data array for year-based template.
 */
import type { FormDataArrayItem, NCSavePayloadDeps } from './ncSavePayload.types';

export function buildNCSavePayload(deps: NCSavePayloadDeps): FormDataArrayItem[] {
  const { packSizeTabs, latestYearFormData, gsmsHndlgType, formatDateForAPI } = deps;
  const formDataArray: FormDataArrayItem[] = [];

  packSizeTabs.forEach(tab => {
    const { conceptName, packSize, index: tabIndex } = tab;

    let conceptEntry = formDataArray.find(c => c.concept_name === conceptName);
    if (!conceptEntry) {
      conceptEntry = {
        concept_name: conceptName,
        pack_size: {},
      };
      formDataArray.push(conceptEntry);
    }

    const packSizeKey = packSize.toString();
    const yearEntries: any[] = [];
    const packSizeYearData = latestYearFormData[tabIndex] || {};

    for (let yearIndex = 0; yearIndex <= 4; yearIndex++) {
      const yearData = packSizeYearData[yearIndex];
      if (!yearData) continue;

      const optionYear = yearIndex + 1;

      const yearEntry: any = {
        option_year: optionYear,
        start_date: formatDateForAPI(yearData.usagePeriodStartDate) || null,
        end_date: formatDateForAPI(yearData.usagePeriodEndDate) || null,
        gsms_mfg_ndc:
          yearData.gsms_mfg_ndc || yearData.mfg_ndc || yearData.selectedMfgNdc || null,
        gsms_ndc: yearData.gsms_ndc || null,
        gsms_cost_from_manufacturer: yearData.gsmsCostFromMfg
          ? parseFloat(yearData.gsmsCostFromMfg)
          : null,
        gsms_net_cost_from_manufacturer: yearData.gsmsNetCostFromMfg
          ? parseFloat(yearData.gsmsNetCostFromMfg)
          : null,
        gsms_iff_fee: yearData.gsmsIffFee ? parseFloat(yearData.gsmsIffFee) : null,
        gsms_cost: yearData.gsmsCost ? parseFloat(yearData.gsmsCost) : null,
        suggested_bid_price:
          yearData.suggestedBidPrice != null && yearData.suggestedBidPrice !== ''
            ? typeof yearData.suggestedBidPrice === 'number'
              ? yearData.suggestedBidPrice
              : parseFloat(yearData.suggestedBidPrice)
            : null,
        wac:
          yearData.wac != null && yearData.wac !== ''
            ? typeof yearData.wac === 'number'
              ? yearData.wac
              : parseFloat(yearData.wac)
            : null,
        supplier_comparable_wac: (() => {
          const value = yearData.supplier_comparable_wac;
          if (value == null || value === '' || value === undefined) return null;
          if (typeof value === 'number') return isNaN(value) ? null : value;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? null : parsed;
        })(),
        gsms_profit:
          yearData.gsmsProfit !== null && yearData.gsmsProfit !== undefined
            ? typeof yearData.gsmsProfit === 'number'
              ? yearData.gsmsProfit
              : parseFloat(yearData.gsmsProfit)
            : null,
        va_annual_nc_est:
          yearData.vaAnnualNcEst != null ? parseFloat(yearData.vaAnnualNcEst) : null,
        dod_annual_nc_est:
          yearData.dodAnnualNcEst != null ? parseFloat(yearData.dodAnnualNcEst) : null,
        ihs_annual_nc_est:
          yearData.ihsAnnualNcEst != null ? parseFloat(yearData.ihsAnnualNcEst) : null,
        bop_annual_nc_est:
          yearData.bopAnnualNcEst != null ? parseFloat(yearData.bopAnnualNcEst) : null,
        nc_est_annual_usage:
          yearData.ncEstAnnualUsage != null ? parseFloat(yearData.ncEstAnnualUsage) : null,
        contract_annual_revenue: yearData.contractAnnualRevenue
          ? parseFloat(yearData.contractAnnualRevenue)
          : null,
        contract_annual_gp: yearData.contractAnnualGP
          ? parseFloat(yearData.contractAnnualGP)
          : null,
        contract_annual_cost: yearData.contractAnnualCost
          ? parseFloat(yearData.contractAnnualCost)
          : null,
        gross_profit: yearData.grossProfit ? parseFloat(yearData.grossProfit) : null,
        gp_per_unit: yearData.gpPerUnit ? parseFloat(yearData.gpPerUnit) : null,
        government_savings_analysis: yearData.govSavingsAnalysis
          ? parseFloat(yearData.govSavingsAnalysis)
          : null,
        gsms_pack_size: (() => {
          if (
            yearData.mfgBottleSize !== null &&
            yearData.mfgBottleSize !== undefined &&
            yearData.mfgBottleSize !== ''
          ) {
            const mfgBottleSizeValue = yearData.mfgBottleSize;
            if (typeof mfgBottleSizeValue === 'number') return Math.round(mfgBottleSizeValue);
            const parsed = parseInt(String(mfgBottleSizeValue).trim(), 10);
            return isNaN(parsed) ? null : parsed;
          }
          return null;
        })(),
        gsms_handling_type: yearData.gsmsHndlgType || gsmsHndlgType || null,
        gsms_bottle_capacity: yearData.gsmsBottleCapacity || null,
        gsms_bottle_cost: yearData.gsmsBottleCost
          ? parseFloat(yearData.gsmsBottleCost)
          : null,
        gsms_cap_cost: yearData.gsmsCapCost ? parseFloat(yearData.gsmsCapCost) : null,
        gsms_label_cost: yearData.gsmsLabelCost ? parseFloat(yearData.gsmsLabelCost) : null,
        gsms_insert_cost: yearData.gsmsInsertCost
          ? parseFloat(yearData.gsmsInsertCost)
          : null,
        gsms_box_or_carton_cost: yearData.gsmsBoxCartonCost
          ? parseFloat(yearData.gsmsBoxCartonCost)
          : null,
        gsms_dessicant_cost: yearData.gsmsDesiccantCost
          ? parseFloat(yearData.gsmsDesiccantCost)
          : null,
        gsms_outsource_repack_fee: yearData.outsourceRepackFee
          ? parseFloat(yearData.outsourceRepackFee)
          : null,
        blended_whls_fee: yearData.blendedWhlsFee
          ? parseFloat(yearData.blendedWhlsFee)
          : null,
        gsms_frt_cost: yearData.gsmsFrtCost ? parseFloat(yearData.gsmsFrtCost) : null,
        pv2_percent_cost: yearData.pv2Percent ? parseFloat(yearData.pv2Percent) : null,
        gsms_labor: yearData.gsmsLabor ? parseFloat(yearData.gsmsLabor) : null,
        mode_wac: (() => {
          const value = yearData.modeWac;
          if (value == null || value === '') return null;
          if (typeof value === 'number') return value;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? null : parsed;
        })(),
        low_wac: (() => {
          const value = yearData.lowWac;
          if (value == null || value === '') return null;
          if (typeof value === 'number') return value;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? null : parsed;
        })(),
        top_rx_price: (() => {
          const value = yearData.topRxPrice;
          if (value == null || value === '') return null;
          if (typeof value === 'number') return value;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? null : parsed;
        })(),
        top_rx_vendor: yearData.topRxVendor || null,
        cost_plus_price: (() => {
          const value = yearData.costPlusPrice;
          if (value == null || value === '') return null;
          if (typeof value === 'number') return value;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? null : parsed;
        })(),
        nadac_price: (() => {
          const value = yearData.nadacPrice;
          if (value == null || value === '') return null;
          if (typeof value === 'number') return value;
          const parsed = parseFloat(value);
          return isNaN(parsed) ? null : parsed;
        })(),
      };

      yearEntries.push(yearEntry);
    }

    conceptEntry.pack_size[packSizeKey] = yearEntries;
  });

  return formDataArray;
}
